/**
 * Live planning sessions: while on, your pointer is shared with everyone on
 * the same page, and you can follow someone as they move around.
 */
import { UserAvatar } from "@/components/avatar";
import { Button } from "@/components/ui/button";
import { colorFor } from "@/lib/collab";
import { presence, usePeers } from "@/lib/presence";
import { liveSessionStore, useLiveSession } from "@/lib/ui";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { Eye, Radio, X } from "lucide-react";
import { useEffect } from "react";

export function LiveCursors() {
  const live = useLiveSession();
  const peers = usePeers();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();

  useEffect(() => {
    if (!live.on) {
      presence.set({ cursor: null });
      return;
    }
    const main = document.querySelector("main");
    if (!main) return;
    const move = (e: PointerEvent) => {
      const r = main.getBoundingClientRect();
      presence.set({ cursor: { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height, scope: location.pathname } });
    };
    const leave = () => presence.set({ cursor: null });
    main.addEventListener("pointermove", move);
    main.addEventListener("pointerleave", leave);
    return () => {
      main.removeEventListener("pointermove", move);
      main.removeEventListener("pointerleave", leave);
    };
  }, [live.on]);

  // Following: go wherever they go.
  const followed = live.following ? peers.find((p) => p.connId === live.following) : undefined;
  useEffect(() => {
    if (followed?.location && followed.location !== pathname) navigate({ to: followed.location as never });
  }, [followed?.location]);

  if (!live.on) return null;
  const here = peers.filter((p) => p.cursor && p.cursor.scope === pathname && p.connId !== presence.myConnId);
  return (
    <>
      <div className="pointer-events-none absolute inset-0 z-40 overflow-hidden">
        {here.map((p) => (
          <div key={p.connId} className="absolute transition-[left,top] duration-75 ease-linear" style={{ left: `${p.cursor!.x * 100}%`, top: `${p.cursor!.y * 100}%` }}>
            <svg width="18" height="18" viewBox="0 0 18 18" style={{ color: colorFor(p.userId) }}>
              <path d="M2 2l5.5 13 2-5.5L15 7.5z" fill="currentColor" stroke="white" strokeWidth="1.2" />
            </svg>
            <span className="ml-3 rounded px-1.5 py-0.5 text-[11px] font-medium text-white" style={{ background: colorFor(p.userId) }}>
              {p.name}
            </span>
          </div>
        ))}
      </div>
      <div className="absolute bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2 rounded-full border bg-popover px-3 py-1.5 text-[12.5px] shadow-lg">
        <Radio className="size-3.5 animate-soft-pulse text-brand" />
        <span className="font-medium">Live session</span>
        <span className="text-muted-foreground">·</span>
        {peers.length === 0 && <span className="text-muted-foreground">No one else is here yet</span>}
        {peers.map((p) => (
          <button
            key={p.connId}
            type="button"
            title={live.following === p.connId ? `Stop following ${p.name}` : `Follow ${p.name}`}
            onClick={() => liveSessionStore.set((s) => ({ ...s, following: s.following === p.connId ? null : p.connId }))}
            className="relative rounded-full"
          >
            <UserAvatar userId={p.userId} size="md" className={live.following === p.connId ? "ring-2 ring-brand" : ""} />
            {live.following === p.connId && <Eye className="absolute -right-1 -bottom-1 size-3 rounded-full bg-brand p-0.5 text-brand-foreground" />}
          </button>
        ))}
        <Button variant="ghost" size="icon-xs" onClick={() => liveSessionStore.set({ on: false, following: null })} aria-label="End live session">
          <X />
        </Button>
      </div>
    </>
  );
}
